/**
 * Extracts every human-readable JSON:API error `detail` from a failed request.
 *
 * Reads `errors[].detail` (also how WarpDrive's thrown `AggregateError`
 * carries the JSON:API errors array), falling back to `content.errors[].detail`
 * (the parsed response body WarpDrive attaches to that error) whenever the
 * top-level array yields no usable details — including when it is empty or
 * holds only detail-less entries. Entries without a non-empty string `detail`
 * are skipped. Returns an empty array when the error carries no details at all.
 */
export interface JsonApiError {
  status?: string | number;
  title?: string;
  detail?: string;
}

/**
 * The first JSON:API error object a failed request carries, whether the thrown value is a
 * plain document, WarpDrive's `AggregateError` (top-level `errors[]`) or a `RequestError`
 * holding the parsed body under `content.errors`. Unlike `extractJsonApiErrorDetails` it
 * keeps `status` and `title`, so callers can label an error even when `detail` is absent.
 * Returns `undefined` when neither location holds an object entry.
 */
export function firstJsonApiError(error: unknown): JsonApiError | undefined {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }
  const { errors, content } = error as { errors?: unknown; content?: { errors?: unknown } };
  const candidates = [errors, content?.errors].flatMap((list) => (Array.isArray(list) ? list : []));
  return candidates.find(
    (entry): entry is JsonApiError => typeof entry === 'object' && entry !== null
  );
}

export function extractJsonApiErrorDetails(error: unknown): string[] {
  if (typeof error !== 'object' || error === null) {
    return [];
  }
  const { errors, content } = error as { errors?: unknown; content?: { errors?: unknown } };
  const topLevelDetails = detailsFrom(errors);
  return topLevelDetails.length > 0 ? topLevelDetails : detailsFrom(content?.errors);
}

function detailsFrom(candidates: unknown): string[] {
  if (!Array.isArray(candidates)) {
    return [];
  }
  return candidates
    .map((entry) =>
      typeof entry === 'object' && entry !== null
        ? (entry as { detail?: unknown }).detail
        : undefined
    )
    .filter((detail): detail is string => typeof detail === 'string' && detail.length > 0);
}

/**
 * Extracts a single human-readable error message from API error responses.
 *
 * Handles common error shapes from JSON:API, WarpDrive, and custom adapter
 * responses:
 * - `{ payload: { detail: string } }` (custom adapter errors)
 * - `{ errors: [{ detail: string }] }` (JSON:API errors, including WarpDrive's
 *   thrown `AggregateError`)
 * - `{ content: { errors: [{ detail: string }] } }` (WarpDrive's parsed body)
 *
 * When several details are present only the first is returned; use
 * {@link extractJsonApiErrorDetails} to surface all of them.
 *
 * @param error - The error object from a failed API request
 * @param fallbackMessage - Message to return if no detail can be extracted
 * @returns The extracted error message or the fallback
 */
export default function parseApiError(error: unknown, fallbackMessage: string): string {
  const err = error as {
    payload?: { detail?: string };
    detail?: string;
  };
  if (err.payload?.detail) {
    return err.payload.detail;
  }
  const [firstDetail] = extractJsonApiErrorDetails(error);
  if (firstDetail) {
    return firstDetail;
  }
  if (err.detail) {
    return err.detail;
  }
  return fallbackMessage;
}
